import React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {getDurationMinutesText} from 'nutshell-core/get-duration-minutes-text';
import {getTypeFromApiId} from 'nutshell-core/utils/get-type-from-api-id';

import {CopyIconButton} from '../button/copy-button/copy-icon-button';
import {colors} from '../colors/colors';
import {IconStatus} from '../icon-status/icon-status';
import {Link} from '../link/link';
import {LinkedEntity} from '../linked-entity/linked-entity';
import {Percentage} from '../percentage/percentage';
import {ProgressBar} from '../progress-bar/progress-bar';
import {StatusBadge} from '../status-badge/status-badge';
import {FormattedTimestamp} from '../timestamp/formatted-timestamp';

import {TableDateTwoLine} from './table-date-two-line';
import {TablePrimaryCell} from './table-primary-cell';
import {TableTimestampComponent} from './table-timestamp-component';
import {TableTwoLineCell} from './table-two-line-cell';
import type {ColumnDataType} from './table-types';

const DEFAULTS: Partial<Record<ColumnDataType, any>> = {
    string: {},
    integer: {
        width: 50,
        minWidth: 50,
        maxWidth: 75,
        rightAlign: true,
        Cell: ({cell}: any) => {
            return typeof cell.value === 'number' ? cell.value.toLocaleString() : cell.value;
        },
    },
    percentage: {
        width: 50,
        minWidth: 50,
        maxWidth: 75,
        rightAlign: true,
        Cell: ({cell}: any) => {
            // Used when percentage is 'N/A'
            if (typeof cell.value === 'string') {
                return cell.value;
            }

            // @ts-expect-error
            return <Percentage value={cell.value} precision={2} />;
        },
        sortType: percentageSortType,
    },
    'date-two-line': {
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        disableSortBy: true,
        Cell: ({cell}: any) => {
            return <TableDateTwoLine date={cell.value} />;
        },
    },
    timestamp: {
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        Cell: ({cell}: any) => {
            const {value} = cell;

            return <TableTimestampComponent timestamp={value} />;
        },
    },
    longTimestamp: {
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        Cell: ({cell}: any) => (
            <FormattedTimestamp
                datetime={cell.value}
                format={FormattedTimestamp.formats.longMonthDayWithYear}
            />
        ),
    },
    duration: {
        width: 90,
        Cell: ({cell}: any) => {
            return getDurationMinutesText(cell.value);
        },
    },
    linkedEntity: {
        width: 110,
        sortType: linkSortType,
        Cell: ({cell}: any) => {
            return (
                <LinkedEntity
                    {...cell.value}
                    type={getTypeFromApiId(cell.value.id)}
                    useEntityColor={true}
                />
            );
        },
    },
    component: {},
    linkedName: {
        Cell: ({cell}: any) => {
            return (
                // @ts-expect-error
                <Link as={RoutedLink} size='small' variant='primary' to={cell.value.htmlUrlPath}>
                    {cell.value.name}
                </Link>
            );
        },
        sortType: linkSortType,
    },
    externalLink: {
        Cell: ({cell}: any) => {
            return (
                <Link size='small' variant='primary' href={cell.value.htmlUrlPath} newTab={true}>
                    {cell.value.name}
                </Link>
            );
        },
    },
    boolean: {
        // @ts-expect-error
        sortType: (rowA, rowB, id) => (rowA.values[id] >= rowB.values[id] ? 1 : -1),
    },
    statusBadge: {
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        // @ts-expect-error
        sortType: (rowA, rowB) => {
            return rowA.values.status.text >= rowB.values.status.text ? 1 : -1;
        },
        Cell: ({cell}: any) => {
            return <StatusBadge {...cell.value} size='small' />;
        },
    },
    copyableString: {
        width: 200,
        Cell: ({cell}: any) => {
            return (
                <div className='flex justify-sb full-width'>
                    <div>{cell.value}</div>
                    <CopyIconButton size='small' text={cell.value} />
                </div>
            );
        },
    },
    iconStatus: {
        width: 200,
        Cell: ({cell}: any) => {
            return <IconStatus {...cell.value} size='small' />;
        },
    },
    usageProgressBar: {
        width: 90,
        minWidth: 80,
        Cell: ({
            cell,
            column,
        }: {
            cell: {
                value: {
                    wordCount: number;
                    isIncluded: boolean;
                };
            };
            column: {
                totalUsage: number;
            };
        }) => {
            const document = cell.value;
            const wordCount = document.isIncluded ? document.wordCount : 0;
            const totalUsage = column.totalUsage || 1;
            const percentOfTotal = totalUsage > 0 ? wordCount / totalUsage : 0;

            return (
                <ProgressBar
                    percentComplete={percentOfTotal}
                    height={4}
                    color={colors.blue}
                    backgroundColor={colors.offWhiteDk}
                    roundedCorners={true}
                    tooltipText={`${wordCount} / ${totalUsage} words`}
                    isFullWidth={true}
                    showPercentage={true}
                />
            );
        },
    },
    primaryWithSubtitle: {
        minWidth: 300,
        Cell: TablePrimaryCell,
    },
    twoLineCell: {
        minWidth: 200,
        Cell: TableTwoLineCell,
    },
};

export function getDefault(columnType: ColumnDataType): any {
    return DEFAULTS[columnType] || {};
}

export function percentageSortType(rowA: any, rowB: any, id: any) {
    if (typeof rowA.values[id] !== 'undefined' && typeof rowB.values[id] !== 'undefined') {
        const rowAPercentage = rowA.values[id];
        const rowBPercentage = rowB.values[id];

        if (rowAPercentage === null && rowBPercentage === null) {
            return 1;
        } else if (rowAPercentage === null) {
            return -1;
        } else if (rowBPercentage === null) {
            return 1;
        }

        return rowAPercentage >= rowBPercentage ? 1 : -1;
    }

    return 0;
}

// @ts-expect-error
function linkSortType(rowA, rowB, id, isSortedDescending) {
    const rowAName = rowA.values[id] ? rowA.values[id].name.toLowerCase() : '';
    const rowBName = rowB.values[id] ? rowB.values[id].name.toLowerCase() : '';

    if (isSortedDescending) {
        return rowAName > rowBName ? 1 : -1;
    }

    return rowAName < rowBName ? -1 : 1;
}
